import { useCallback } from 'react'
import { useUrlState } from './useUrlState'

export interface MatchesFilters {
  startDate: string
  endDate: string
  playerIds: string[]
  competitionIds: string[]
  courtIds: string[]
  publishingStatuses: string[]
  categories: string[]
  search: string
  sortOrder: 'asc' | 'desc'
  page: number
}

/**
 * Hook for managing matches list filters in URL
 * Provides type-safe filter state and updater
 */
export function useMatchesFilters() {
  // Calculate default dates (last 7 days to next 7 days)
  const getDefaultStartDate = () => {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    return date.toISOString().split('T')[0]
  }

  const getDefaultEndDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 7)
    return date.toISOString().split('T')[0]
  }

  const defaultFilters: MatchesFilters = {
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    playerIds: [],
    competitionIds: [],
    courtIds: [],
    publishingStatuses: [],
    categories: [],
    search: '',
    sortOrder: 'desc',
    page: 1,
  }

  const { state, updateState } = useUrlState<MatchesFilters>(
    defaultFilters,
    {
      // Serialize/deserialize arrays as comma-separated strings
      playerIds: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      competitionIds: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      courtIds: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      publishingStatuses: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      categories: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      // Search is a simple string - only include in URL if not empty
      search: {
        serialize: (value) => (value && value.trim() ? value.trim() : undefined),
        deserialize: (value) => (value || ''),
      },
      // Parse page as number
      page: {
        serialize: (value) => (value > 1 ? String(value) : undefined),
        deserialize: (value) => (value ? parseInt(value, 10) : 1),
      },
    }
  )

  /** Update filters and reset to page 1 */
  const updateFilterAndResetPage = useCallback(
    (updates: Partial<MatchesFilters>) => {
      updateState({ ...updates, page: 1 })
    },
    [updateState]
  )

  /** Change page and scroll to top */
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateState({ page: newPage })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [updateState]
  )

  return {
    filters: state,
    updateFilters: updateState,
    updateFilterAndResetPage,
    handlePageChange,
  }
}

